import { useMemo } from 'react';

import {
  getSaleCouponCode,
  getSaleCouponDiscountCents,
  getSaleCouponPercentLabel,
  getSaleDiscountCents,
  getSaleItems,
  getSaleProductCount,
  getSaleServiceFeeDescriptions,
  getSaleSubtotalCents,
  getSaleTotalAmountCents,
  type Sale,
} from '@src/domains/sales';
import { getCouponAffectedProductNames, shouldShowCouponAffectedProducts } from '@src/domains/coupons';
import { shouldShowServiceFee } from '@src/domains/service-fees';
import { saleSummaryHasDetails } from '@src/features/sales/summary/core/saleSummaryHasDetails';
import useFeatureFlag from '@src/hooks/useFeatureFlag';

export type UseSaleSummaryResult = {
  productCount: number;
  subtotalCents: number;
  totalAmountCents: number;
  discountCents: number;
  couponCode?: string;
  couponDiscountCents: number;
  couponPercentLabel?: string;
  affectedProductNames: string[];
  showAffectedProducts: boolean;
  showServiceFee: boolean;
  serviceFeeAmountCents: number;
  serviceFeeDescription?: string;
  hasDetails: boolean;
  items: ReturnType<typeof getSaleItems>;
};

export const useSaleSummary = (sale: Sale | null | undefined): UseSaleSummaryResult => {
  const { isFeatureFlagEnabled } = useFeatureFlag();
  const serviceFeeFeatureEnabled = isFeatureFlagEnabled('PR-647-service-fee');

  const totalDiscountCents = getSaleDiscountCents(sale);
  const couponDiscountCents = getSaleCouponDiscountCents(sale);
  const showServiceFee = shouldShowServiceFee(sale?.serviceFeeCurrencyInfo?.amountCents, serviceFeeFeatureEnabled);
  const serviceFeeAmountCents = sale?.serviceFeeCurrencyInfo?.amountCents ?? 0;
  const serviceFeeDescription = useMemo(() => getSaleServiceFeeDescriptions(sale).join(', ') || undefined, [sale]);
  const items = useMemo(() => getSaleItems(sale), [sale]);
  const affectedProductNames = getCouponAffectedProductNames(sale?.coupon, items);

  return {
    productCount: getSaleProductCount(sale),
    subtotalCents: getSaleSubtotalCents(sale),
    totalAmountCents: getSaleTotalAmountCents(sale),
    discountCents: totalDiscountCents,
    couponCode: getSaleCouponCode(sale),
    couponDiscountCents,
    couponPercentLabel: getSaleCouponPercentLabel(sale),
    affectedProductNames,
    showAffectedProducts: shouldShowCouponAffectedProducts(sale?.coupon, items),
    showServiceFee,
    serviceFeeAmountCents,
    serviceFeeDescription,
    hasDetails: saleSummaryHasDetails(sale, serviceFeeFeatureEnabled),
    items,
  };
};

export default useSaleSummary;
